import { Box, Stack, Text, Wrap, WrapItem } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiFileText } from "react-icons/fi";
import type { PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";
import { formatCpv, formatShortDate, getProcedureLabel } from "../utils/displayFormatters";
import { AwardCriteriaContent } from "./EventAwardCriteriaSection";
import { usePortalProcurementTerms } from "../../../../common/terminology/useProcurementTerms";
import { LabelValueGrid } from "./LabelValueGrid";
import { SectionCard } from "./SectionCard";
import { isNegotiationWithoutNotice } from "../../buyer/wizard/utils/negotiationWithoutNotice";

export const EventAboutSection = ({
  event,
  showAwardCriteria = false,
}: {
  event: PortalSourcingEventDetailDto;
  showAwardCriteria?: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const terms = usePortalProcurementTerms(event);
  const locale = momentLocaleFor(i18n.language);
  const hasDescription = !isTiptapContentEmpty(event.description);
  const hasCpvCodes = (event.cpvCodes?.length ?? 0) > 0;
  const hasAwardCriteria = showAwardCriteria && !!event.awardCriteria;
  const procedureRows = [
    event.procedureType && { label: t("Procedure"), value: getProcedureLabel(event.procedureType, t) },
    event.publishedAt && { label: isNegotiationWithoutNotice(event.procedureType) ? t("Invitations sent") : t("Announced"), value: formatShortDate(event.publishedAt, locale) },
    event.dialoguePlanned != null && { label: t("Dialogue planned"), value: event.dialoguePlanned ? t("Yes") : t("No") },
  ].filter((row): row is { label: string; value: string } => Boolean(row));

  if (!hasDescription && !hasCpvCodes && procedureRows.length === 0 && !hasAwardCriteria) return null;

  return (
    <SectionCard icon={FiFileText} title={t("About this procurement")}>
      <Stack spacing="6">
        {hasDescription && (
          <TipTap
            content={event.description ?? ""}
            editable={false}
            hasBorder={false}
            maxHeight=""
            padding="0"
          />
        )}

        {hasCpvCodes && (
          <Stack spacing="2">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="smMuted"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              {t("CPV codes")}
            </Text>
            <Wrap spacing="2">
              {event.cpvCodes?.map((cpv) => (
                <WrapItem key={cpv.code}>
                  <Box
                    bg="smPrimarySofter"
                    color="smPrimary"
                    px="2.5"
                    py="1"
                    rounded="md"
                    fontSize="xs"
                    fontWeight="medium"
                  >
                    {formatCpv(cpv, i18n.language)}
                  </Box>
                </WrapItem>
              ))}
            </Wrap>
          </Stack>
        )}

        {procedureRows.length > 0 && <LabelValueGrid rows={procedureRows} />}

        {event.dialoguePlanned === true && !isTiptapContentEmpty(event.dialogueDescription) && (
          <Stack spacing="2">
            <Text fontSize="xs" fontWeight="semibold" color="smMuted" textTransform="uppercase" letterSpacing="wide">
              {t("What the dialogue will cover")}
            </Text>
            <TipTap
              content={event.dialogueDescription ?? ""}
              editable={false}
              hasBorder={false}
              maxHeight=""
              padding="0"
            />
          </Stack>
        )}

        {hasAwardCriteria && (
          <Stack spacing="3">
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="smMuted"
              textTransform="uppercase"
              letterSpacing="wide"
            >
              {terms.awardCriteria}
            </Text>
            <AwardCriteriaContent event={event} />
          </Stack>
        )}
      </Stack>
    </SectionCard>
  );
};
